/**
 * User Store Cache Service
 * Manages user-specific store settings loaded from /email_users/{uid}
 * Cached in localStorage for persistence across sessions
 */

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase.config';
import { useUserStore, useFirestoreCacheStatus } from '../stores';

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize user store cache with real-time listener
 * @param {string} uid - User's Firebase UID
 * @returns {Function} Unsubscribe function
 */
export const initializeUserStoreCache = (uid) => {
  if (!uid) {
    console.error('❌ Cannot initialize user store cache: No UID provided');
    return () => {};
  }

  console.log(`🏪 Initializing user store cache for UID: ${uid}`);

  // Try to load from Firestore cache immediately (one-time fetch)
  // This will use Firestore persistent cache if available
  getDoc(doc(db, 'email_users', uid))
    .then((docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        useUserStore.getState().setUserStore(data);
      }
    })
    .catch((error) => {
      console.error('❌ Error loading user store on init:', error);
    });

  // Set up real-time listener
  const userStoreRef = doc(db, 'email_users', uid);
  
  const unsubscribe = onSnapshot(
    userStoreRef,
    { includeMetadataChanges: true },
    (docSnapshot) => {
      // Update Firestore cache status
      const isFromCache = docSnapshot.metadata.fromCache;
      const setServingCache = useFirestoreCacheStatus.getState().setServingCache;
      if (isFromCache) {
        setServingCache(true);
      }
      
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        // Update Zustand store
        useUserStore.getState().setUserStore(data);
      } else {
        console.warn('⚠️ User store document does not exist for UID:', uid);
        useUserStore.getState().clearUserStore();
      }
    },
    (error) => {
      console.error('❌ Error in user store subscription:', error);
    }
  );

  return unsubscribe;
};

/**
 * Get user store data from cache (synchronous)
 * @returns {Object|null} User store data or null
 */
export const getUserStoreFromCache = () => {
  // Read from Zustand store (populated by onSnapshot listener)
  try {
    return useUserStore.getState().userStore;
  } catch (error) {
    console.error('❌ Error reading user store from Zustand:', error);
    return null;
  }
};

// localStorage caching functions removed - using Firestore persistent cache only

/**
 * Load user store data from Firestore (one-time fetch)
 * @param {string} uid - User's Firebase UID
 * @returns {Promise<Object|null>} User store data or null
 */
export const loadUserStore = async (uid) => {
  if (!uid) {
    console.error('❌ Cannot load user store: No UID provided');
    return null;
  }

  try {
    console.log(`🔄 Loading user store for UID: ${uid}`);
    const userStoreRef = doc(db, 'email_users', uid);
    const docSnapshot = await getDoc(userStoreRef);

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      console.log('✅ User store loaded from Firestore:', data);
      
      // Update Zustand store
      useUserStore.getState().setUserStore(data);
      
      return data;
    } else {
      console.warn('⚠️ User store document does not exist for UID:', uid);
      return null;
    }
  } catch (error) {
    console.error('❌ Error loading user store:', error);
    // Return from Zustand store as fallback
    return getUserStoreFromCache();
  }
};

/**
 * Get specific field from user store cache
 * @param {string} fieldName - Field name to retrieve
 * @param {*} defaultValue - Default value if field doesn't exist
 * @returns {*} Field value or default value
 */
export const getUserStoreField = (fieldName, defaultValue = null) => {
  const userStore = getUserStoreFromCache();
  if (!userStore) return defaultValue;
  
  return userStore[fieldName] !== undefined ? userStore[fieldName] : defaultValue;
};

/**
 * Get tenant ID from cached user store
 * @returns {string|null} Tenant ID or null
 */
export const getTenantId = () => {
  // Check both tenantId (lowercase) and tenantID (uppercase) for backward compatibility
  const tenantId = getUserStoreField('tenantID', null) || getUserStoreField('tenantId', null);
  return tenantId;
};

/**
 * Get store ID from cached user store
 * @returns {string|null} Store ID or null
 */
export const getStoreId = () => {
  // Check both storeId (lowercase) and storeID (uppercase) for backward compatibility
  const storeId = getUserStoreField('storeID', null) || getUserStoreField('storeId', null);
  return storeId;
};

/**
 * Get full tenant/store context
 * @returns {Object} Object with tenantId and storeId
 */
export const getTenantStoreContext = () => {
  const userStore = getUserStoreFromCache();
  return {
    tenantId: userStore?.tenantId || userStore?.tenantID || null,
    storeId: userStore?.storeId || userStore?.storeID || null,
  };
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getUserStoreCacheStats = () => {
  const data = getUserStoreFromCache();
  
  return {
    isCached: !!data,
    lastUpdated: null, // No longer tracking timestamp - using Firestore cache
    fields: data ? Object.keys(data) : [],
  };
};

/**
 * Clear user store cache
 * Clears the Zustand store data (called on logout)
 */
export const clearUserStoreCache = () => {
  try {
    useUserStore.getState().clearUserStore();
    console.log('✅ User store cache cleared');
  } catch (error) {
    console.error('❌ Error clearing user store cache:', error);
  }
};

